import {
  CANVAS_WIDTH_UNITS,
  CANVAS_HEIGHT_UNITS,
  POWERPOINT_WIDTH_INCHES,
  POWERPOINT_HEIGHT_INCHES,
} from "constants/powerpointCanvas"

import { ELEMENT_TYPE } from "types/ReportingDashboard/elements.types"

// --- ENHANCED TYPE DEFINITIONS ---
export interface CanvasToPptOptions {
  screenScaling?: number
  verticalOffset?: number
  compressionFactor?: number
  verticalAdjustment?: number // Fine-tuning vertical position
  horizontalAdjustment?: number // Fine-tuning horizontal position
  elementType?: ELEMENT_TYPE // Element type for specific adjustments
  canvasScale?: number // NEW: Current canvas scale factor for responsive scaling
  // New: Add reference point calibration
  referenceElement?: {
    canvasPos: { x: number; y: number; w: number; h: number }
    expectedPptPos: { x: number; y: number; w: number; h: number }
  }
}

export interface PptCoordinates {
  x: number
  y: number
  w: number
  h: number
}

export interface CompressionOptions {
  compressionFactor?: number
  fontSize?: number
  cellPadding?: number
}

export interface ElementPositionData {
  // Store both display and logical coordinates
  logicalX: number // Position independent of screen scaling
  logicalY: number // Position independent of screen scaling
  logicalWidth: number // Size independent of screen scaling
  logicalHeight: number // Size independent of screen scaling
  displayX: number // Visual position on current screen
  displayY: number // Visual position on current screen
  displayWidth: number // Visual size on current screen
  displayHeight: number // Visual size on current screen
  screenDPI: number // Screen DPI when positioned
  canvasScale: number // Canvas scale factor when positioned
}

// --- CONSTANTS ---
const BASELINE_WIDTH = 1440 // Standard canvas width
const BASELINE_HEIGHT = 810 // Standard canvas height
const BASELINE_DPI = 96 // Standard DPI

// --- UTILITY FUNCTIONS ---

/**
 * Convert screen-dependent coordinates to logical coordinates
 */
const displayToLogical = (
  displayX: number,
  displayY: number,
  displayW: number,
  displayH: number,
  currentCanvasScale: number = 1,
  currentScreenDPI: number = 96
): ElementPositionData => {
  // Account for canvas scaling (zoom, responsive scaling)
  const logicalX = displayX / currentCanvasScale
  const logicalY = displayY / currentCanvasScale
  const logicalWidth = displayW / currentCanvasScale
  const logicalHeight = displayH / currentCanvasScale

  // Account for DPI differences
  const dpiScaleFactor = BASELINE_DPI / currentScreenDPI

  return {
    logicalX: logicalX * dpiScaleFactor,
    logicalY: logicalY * dpiScaleFactor,
    logicalWidth: logicalWidth * dpiScaleFactor,
    logicalHeight: logicalHeight * dpiScaleFactor,
    displayX,
    displayY,
    displayWidth: displayW,
    displayHeight: displayH,
    screenDPI: currentScreenDPI,
    canvasScale: currentCanvasScale,
  }
}

/**
 * Convert logical coordinates to PowerPoint with scale-aware spacing preservation
 */
const logicalToPowerPointWithScaleAwareSpacing = (
  logicalX: number,
  logicalY: number,
  logicalW: number,
  logicalH: number,
  canvasScale: number = 1.0
): PptCoordinates => {
  // Enhanced conversion ratios that better preserve canvas spacing
  const xRatio = POWERPOINT_WIDTH_INCHES / BASELINE_WIDTH
  const yRatio = POWERPOINT_HEIGHT_INCHES / BASELINE_HEIGHT

  // ENHANCED: Apply scale-aware spacing correction
  // When canvas is smaller (scale < 1), elements appear closer together
  // So we need to reduce spacing in PowerPoint to match visual appearance
  const scaleAwareSpacingCorrectionX = 0.95 * canvasScale
  const scaleAwareSpacingCorrectionY = 1.0 * canvasScale

  // Calculate center-based positioning for better proportional spacing
  const canvasCenterX = BASELINE_WIDTH / 2
  const canvasCenterY = BASELINE_HEIGHT / 2
  const pptCenterX = POWERPOINT_WIDTH_INCHES / 2
  const pptCenterY = POWERPOINT_HEIGHT_INCHES / 2

  // Calculate offset from center
  const offsetFromCenterX = logicalX + logicalW / 2 - canvasCenterX
  const offsetFromCenterY = logicalY + logicalH / 2 - canvasCenterY

  // Apply scale-aware spacing correction to the offset
  const correctedOffsetX = offsetFromCenterX * scaleAwareSpacingCorrectionX
  const correctedOffsetY = offsetFromCenterY * scaleAwareSpacingCorrectionY

  // Calculate final position
  const pptCenterElementX = pptCenterX + correctedOffsetX * xRatio
  const pptCenterElementY = pptCenterY + correctedOffsetY * yRatio

  // Convert back to top-left coordinates
  const pptX = pptCenterElementX - (logicalW * xRatio) / 2
  const pptY = pptCenterElementY - (logicalH * yRatio) / 2
  const pptW = logicalW * xRatio
  const pptH = logicalH * yRatio

  // Ensure elements stay within slide boundaries
  const finalX = Math.max(0, Math.min(pptX, POWERPOINT_WIDTH_INCHES - pptW))
  const finalY = Math.max(0, Math.min(pptY, POWERPOINT_HEIGHT_INCHES - pptH))
  const finalW = Math.min(pptW, POWERPOINT_WIDTH_INCHES - finalX)
  const finalH = Math.min(pptH, POWERPOINT_HEIGHT_INCHES - finalY)

  return {
    x: Math.round(finalX * 1000) / 1000,
    y: Math.round(finalY * 1000) / 1000,
    w: Math.round(finalW * 1000) / 1000,
    h: Math.round(finalH * 1000) / 1000,
  }
}

// --- MAIN CONVERSION FUNCTIONS ---

/**
 * Enhanced canvas to PowerPoint conversion with scale-aware spacing
 */
export const canvasToPowerPointCoordsEnhanced = (
  canvasX: number,
  canvasY: number,
  canvasWidth: number,
  canvasHeight: number,
  options: CanvasToPptOptions = {}
): PptCoordinates => {
  const {
    verticalOffset = 0,
    compressionFactor = 1.0,
    screenScaling = 1.0,
    elementType,
    referenceElement,
    // Add canvas scale to account for responsive scaling
    canvasScale = 1.0,
  } = options

  // Get element-specific adjustments (simplified)
  let finalVerticalAdjustment = 0.12
  let finalHorizontalAdjustment = 0.0

  if (elementType) {
    const elementAdjustments = getElementPositioningAdjustments(elementType)
    finalVerticalAdjustment = elementAdjustments.verticalAdjustment
    finalHorizontalAdjustment = elementAdjustments.horizontalAdjustment
  }

  // Get current screen info for calibration
  const currentScreenDPI = window.devicePixelRatio * 96

  // IMPORTANT: Apply canvas scale to dimensions to reflect actual visual size
  const scaledCanvasX = canvasX
  const scaledCanvasY = canvasY
  const scaledCanvasWidth = canvasWidth * canvasScale
  const scaledCanvasHeight = canvasHeight * canvasScale

  // Convert display coordinates to logical coordinates using scaled dimensions
  const logicalData = displayToLogical(
    scaledCanvasX,
    scaledCanvasY,
    scaledCanvasWidth,
    scaledCanvasHeight,
    screenScaling,
    currentScreenDPI
  )

  // Apply reference element calibration if provided (simplified)
  let calibratedLogical = logicalData
  if (referenceElement) {
    const refLogical = displayToLogical(
      referenceElement.canvasPos.x,
      referenceElement.canvasPos.y,
      referenceElement.canvasPos.w * canvasScale, // Scale reference too
      referenceElement.canvasPos.h * canvasScale,
      screenScaling,
      currentScreenDPI
    )

    // Simplified calibration - only apply small corrections
    const expectedRefPpt = referenceElement.expectedPptPos
    const actualRefPpt = logicalToPowerPointWithScaleAwareSpacing(
      refLogical.logicalX,
      refLogical.logicalY,
      refLogical.logicalWidth,
      refLogical.logicalHeight,
      canvasScale // Pass canvas scale for spacing calculation
    )

    const offsetX = (expectedRefPpt.x - actualRefPpt.x) * 0.5 // Reduce calibration impact
    const offsetY = (expectedRefPpt.y - actualRefPpt.y) * 0.5

    calibratedLogical = {
      ...logicalData,
      logicalX:
        logicalData.logicalX + offsetX / (POWERPOINT_WIDTH_INCHES / 1440),
      logicalY:
        logicalData.logicalY + offsetY / (POWERPOINT_HEIGHT_INCHES / 810),
    }
  }

  // Convert to PowerPoint coordinates using scale-aware spacing preservation
  const pptCoords = logicalToPowerPointWithScaleAwareSpacing(
    calibratedLogical.logicalX,
    calibratedLogical.logicalY + verticalOffset,
    calibratedLogical.logicalWidth * compressionFactor,
    calibratedLogical.logicalHeight * compressionFactor,
    canvasScale // CRITICAL: Pass canvas scale for spacing calculation
  )

  // Apply minimal element-specific adjustments
  return {
    x: pptCoords.x + finalHorizontalAdjustment,
    y: pptCoords.y + finalVerticalAdjustment,
    w: pptCoords.w,
    h: pptCoords.h,
  }
}

/**
 * Get current canvas scaling information with actual scale factor
 */
export const getCanvasScalingInfo = (
  canvasRef: React.RefObject<HTMLElement>
) => {
  if (!canvasRef.current) {
    return {
      scaleX: 1,
      scaleY: 1,
      actualDPI: 96,
      viewportScale: 1,
      canvasScale: 1,
    }
  }

  const canvas = canvasRef.current
  const rect = canvas.getBoundingClientRect()

  // Calculate the actual canvas scale factor
  const canvasScale = rect.width / CANVAS_WIDTH_UNITS

  return {
    scaleX: rect.width / CANVAS_WIDTH_UNITS,
    scaleY: rect.height / CANVAS_HEIGHT_UNITS,
    actualDPI: window.devicePixelRatio * 96,
    viewportScale: window.visualViewport?.scale || 1,
    canvasScale: canvasScale, // Add this for visual scaling
  }
}

/**
 * Create reference points for calibration with corrected vertical positioning
 */
export const createCalibrationReference = () => {
  return {
    topLeft: {
      canvasPos: { x: 0, y: 0, w: 50, h: 50 },
      expectedPptPos: { x: 0, y: 0.1, w: 0.347, h: 0.309 }, // Slight Y offset added
    },
    center: {
      canvasPos: { x: 720, y: 405, w: 100, h: 100 },
      expectedPptPos: { x: 5, y: 2.9125, w: 0.694, h: 0.618 }, // Adjusted Y position
    },
    bottomRight: {
      canvasPos: { x: 1390, y: 760, w: 50, h: 50 },
      expectedPptPos: { x: 9.653, y: 5.416, w: 0.347, h: 0.309 }, // Adjusted Y position
    },
  }
}

/**
 * Get element-specific positioning adjustments with simplified approach
 */
export const getElementPositioningAdjustments = (elementType: ELEMENT_TYPE) => {
  switch (elementType) {
    case ELEMENT_TYPE.IMAGE:
      return {
        horizontalAdjustment: -0.32, // Keep image adjustment
        verticalAdjustment: 0.15,
      }
    case ELEMENT_TYPE.CHART:
    case ELEMENT_TYPE.PIE_CHART:
    case ELEMENT_TYPE.TIME_SERIES_CHART:
      return {
        horizontalAdjustment: 0.0, // Remove chart-specific adjustments - let spacing correction handle it
        verticalAdjustment: 0.12,
      }
    case ELEMENT_TYPE.TABLE:
      return {
        horizontalAdjustment: 0.0, // Remove table adjustment - let spacing correction handle it
        verticalAdjustment: 0.15,
      }
    case ELEMENT_TYPE.TEXT:
    case ELEMENT_TYPE.HEADING:
      return {
        horizontalAdjustment: -0.02,
        verticalAdjustment: 0.12,
      }
    default:
      return {
        horizontalAdjustment: 0.0,
        verticalAdjustment: 0.12,
      }
  }
}

// --- ORIGINAL FUNCTIONS (BACKWARD COMPATIBILITY) ---

export const calculatePptFontSize = (
  elementType: ELEMENT_TYPE.HEADING | ELEMENT_TYPE.TEXT,
  elementFontSize: number | undefined
): number => {
  const defaultFontSize = elementType === ELEMENT_TYPE.HEADING ? 24 : 16
  const fontSize = elementFontSize || defaultFontSize
  const scaleFactor = elementType === ELEMENT_TYPE.HEADING ? 0.65 : 0.6
  return Math.round(fontSize * scaleFactor)
}

export const canvasToPowerPointCoords = (
  canvasX: number,
  canvasY: number,
  canvasWidth: number,
  canvasHeight: number,
  options: CanvasToPptOptions = {}
): PptCoordinates => {
  const { verticalOffset = 0, compressionFactor = 1.0 } = options

  // Direct conversion without scaling modifications
  // The canvas units (1440x810) map directly to PowerPoint inches (10x5.625)
  const conversionX = POWERPOINT_WIDTH_INCHES / CANVAS_WIDTH_UNITS // 10/1440
  const conversionY = POWERPOINT_HEIGHT_INCHES / CANVAS_HEIGHT_UNITS // 5.625/810

  // Convert canvas coordinates to PowerPoint inches
  const ppX = canvasX * conversionX
  const ppY = canvasY * conversionY + verticalOffset
  const ppWidth = canvasWidth * conversionX * compressionFactor
  const ppHeight = canvasHeight * conversionY * compressionFactor

  // Ensure elements don't go off the slide
  const finalX = Math.max(0, Math.min(ppX, POWERPOINT_WIDTH_INCHES - ppWidth))
  const finalY = Math.max(0, Math.min(ppY, POWERPOINT_HEIGHT_INCHES - ppHeight))
  const finalW = Math.min(ppWidth, POWERPOINT_WIDTH_INCHES - finalX)
  const finalH = Math.min(ppHeight, POWERPOINT_HEIGHT_INCHES - finalY)

  return {
    x: Math.round(finalX * 100) / 100,
    y: Math.round(finalY * 100) / 100,
    w: Math.round(finalW * 100) / 100,
    h: Math.round(finalH * 100) / 100,
  }
}
